/**
 * REST client for the gateway WebAPI endpoints used by Edge Live Monitor.
 *
 * Base URL = `(window as any).app.config.machineGatewayApiBaseUrl`. This is a
 * NEW config field — added to public/config/config.js (dev) and the IIS
 * config.cc.js / config.prod.js / config.qa.js / config.stage.js. On dev it
 * points at `http://localhost:5xxx` (the gateway WebAPI port); on `.cc` / `.com`
 * it points at `https://app.machine-id.cc/api/machinegateway` (the IIS app
 * mount).
 *
 * Endpoints (all use the existing JWT cookie/bearer token interceptor):
 *   GET /v1/gateway/admin/site-agents              — LIST (admin-gated server-side)
 *   GET /v1/gateway/machines/{id}/status           — Machinery:Read
 *   GET /v1/gateway/machines/{id}/events?limit=50  — Machinery:Read
 *
 * Per spec §6.2 (no role gate v1) — the page calls all three endpoints and
 * gracefully handles 403 / empty responses (renders `—` or "no access").
 */

import api from "../../core/api";
import {
  GatewayMachineSummary,
  MachineEventDto,
  MachineStatusDto,
  SiteAgentDto,
} from "./models/edgeMonitorModels";
import {
  BotAuditLogPageDto,
  EdgeHubStatusDto,
  SiteAgentDetailDto,
  SymbolCatalogStatusDto,
} from "./models/edgeMonitorAdminModels";

const getRequestConfig = () => {
  const baseURL = (window as any).app?.config?.machineGatewayApiBaseUrl;
  return baseURL ? { baseURL } : {};
};

class EdgeMonitorApi {
  /**
   * LIST endpoint — Admin-gated server-side. Non-Admin users see 403 and the
   * page renders the SiteAgent fields blank per §6.2. Returns null on 403/error
   * so the caller can render gracefully.
   */
  getSiteAgents(): Promise<SiteAgentDto[] | null> {
    return api
      .get<SiteAgentDto[]>(`/v1/gateway/admin/site-agents`, getRequestConfig())
      .then((x) => x.data)
      .catch(() => null);
  }

  /**
   * Returns null if the machine has no status row yet, OR if the user lacks
   * Machinery:Read on the machine's customer (server returns 200 with null body
   * on no-row, 403 on no-access — both collapse to null here).
   */
  getStatus(machineId: string): Promise<MachineStatusDto | null> {
    return api
      .get<MachineStatusDto | null>(
        `/v1/gateway/machines/${machineId}/status`,
        getRequestConfig()
      )
      .then((x) => x.data ?? null)
      .catch(() => null);
  }

  /**
   * Returns the most recent `limit` events (newest first per server). Empty
   * array on error/no-access.
   */
  getEvents(machineId: string, limit: number = 50): Promise<MachineEventDto[]> {
    return api
      .get<MachineEventDto[]>(
        `/v1/gateway/machines/${machineId}/events`,
        { ...getRequestConfig(), params: { limit } }
      )
      .then((x) => x.data ?? [])
      .catch(() => []);
  }

  /**
   * LIST endpoint — Machinery:Read, customer-scoped. Returns connected
   * machines sorted server-side (agentOnline desc, lastDataReceived desc,
   * name asc). Drives the Zone 1 machine picker dropdown. Returns empty
   * array on error/no-access so the UI degrades gracefully.
   */
  listMachines(): Promise<GatewayMachineSummary[]> {
    return api
      .get<GatewayMachineSummary[]>(`/v1/gateway/machines`, getRequestConfig())
      .then((x) => x.data ?? [])
      .catch(() => []);
  }

  // -----------------------------------------------------------------------
  // EDGE-MONITOR-V2 Phase A — admin endpoints (§4.1, §4.2, §4.3, §4.4)
  // All four are Admin-gated server-side. Non-admin → 403; cross-tenant
  // (machineId belongs to another customer) → 404 MACHINE_NOT_FOUND. Both
  // collapse to null here so the UI degrades gracefully (tile renders blank
  // / "no access" caption per parent spec §6.2).
  // -----------------------------------------------------------------------

  /** §4.1 EdgeAgentHub connection state for a machine's SiteAgent. */
  getEdgeHubStatus(machineId: string): Promise<EdgeHubStatusDto | null> {
    return api
      .get<EdgeHubStatusDto>(`/v1/gateway/admin/edge-hub-status`, {
        ...getRequestConfig(),
        params: { machineId },
      })
      .then((x) => x.data)
      .catch(() => null);
  }

  /** §4.2 Symbol catalog seed status (row count + last seeded). */
  getSymbolCatalogStatus(machineId: string): Promise<SymbolCatalogStatusDto | null> {
    return api
      .get<SymbolCatalogStatusDto>(`/v1/gateway/admin/symbol-catalog-status`, {
        ...getRequestConfig(),
        params: { machineId },
      })
      .then((x) => x.data)
      .catch(() => null);
  }

  /**
   * §4.3 Last N rows of `BotApiAuditLog` for the machine. Caller may pass any
   * `limit`; the server clamps to <= 200 (SEC-REQ-5). Empty page on no-access.
   */
  getBotAuditLog(machineId: string, limit: number = 50): Promise<BotAuditLogPageDto | null> {
    return api
      .get<BotAuditLogPageDto>(`/v1/gateway/admin/bot-audit-log`, {
        ...getRequestConfig(),
        params: { machineId, limit },
      })
      .then((x) => x.data)
      .catch(() => null);
  }

  /** §4.4 Single SiteAgent detail row for the per-machine inline tile. */
  getSiteAgentDetail(siteAgentId: string): Promise<SiteAgentDetailDto | null> {
    return api
      .get<SiteAgentDetailDto>(
        `/v1/gateway/admin/site-agents/${siteAgentId}/detail`,
        getRequestConfig()
      )
      .then((x) => x.data)
      .catch(() => null);
  }
}

const edgeMonitorApi = new EdgeMonitorApi();
export default edgeMonitorApi;
